import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {SchemaInterfaceExtendingSchema, TagSchema} from 'constants/RegistryConstants';
import {Schema} from 'schemas/SchemaConstants';
import {isSchemaInterface, isTagSchema} from 'schemas/SchemaPredicates';

import QueryingTagSchemaOrInterfaceSelector, {
  TagSchemaOrInterfaceSelector,
  TagSchemaOrInterfaceSelectorBaseProps,
  TagSchemaOrInterfaceSelectorProps,
} from './TagSchemaOrInterfaceSelector';

export interface TagSchemaSelectorProps extends TagSchemaOrInterfaceSelectorBaseProps {
  selectedTagSchemaId?: string | null;
  // The full TagSchema object corresponding to value if we want to force the selector to show it even if
  // it is not a valid selection (either user doesn't have permission or it was deleted.)
  forceVisibleSelectedTagSchema?: TagSchema | null;
  // The TagSchema could be null if `allowNone` is true.  Otherwise, it should not be null.
  onChange: (tagSchema: TagSchema | null) => void;
  // Function from tag schema to boolean specifying whether the tag schema should be disabled in the list.
  isTagSchemaDisabledFn?: (schema: TagSchema) => boolean;
}

export interface ConnectedProps {
  starredSchemaIds: Array<string> | null;
  tagSchemas: Array<TagSchema> | null;
  getSchemaOwner: (schema: {id: string; name: string}) => string | undefined;
}

function transformTagSchemaFunctionToTagSchemaOrInterfaceFunction<T>(
  tagSchemaFn: undefined | ((schema: TagSchema) => T),
  schemaInterfaceValue: T
): undefined | ((schema: TagSchema | SchemaInterfaceExtendingSchema) => T) {
  if (tagSchemaFn === undefined) {
    return undefined;
  }
  return (schema: TagSchema | SchemaInterfaceExtendingSchema) => {
    if (isSchemaInterface(schema)) {
      assertAndContinue(
        isTagSchema(schema),
        'Unexpectedly did not recieve tag schema on a TagSchema only selector'
      );
      return schemaInterfaceValue;
    } else {
      return tagSchemaFn(schema);
    }
  };
}

const transformTagSchemaSelectorPropsToTagSchemaOrInterfaceSelectorProps = (
  onChange: (tagSchema: TagSchema | null) => void,
  selectedTagSchemaId: string | null | undefined,
  forceVisibleSelectedTagSchema: TagSchema | null | undefined,
  isTagSchemaDisabledFn: (schema: TagSchema) => boolean,
  tagSchemaFilterFn: ((schema: TagSchema) => boolean) | undefined,
  allowNone: boolean
): TagSchemaOrInterfaceSelectorProps => {
  const tagSchemaChangeHandler = (value: Schema | null): void => {
    if (value == null) {
      assert(Boolean(allowNone), 'Selected null for schema but allowNone not set');
      onChange(null);
    } else if (isTagSchema(value)) {
      onChange(value);
    } else {
      assert(false, 'Value is not a tag schema or null');
    }
  };
  return {
    onChange: tagSchemaChangeHandler,
    selectedTagSchemaOrInterfaceId: selectedTagSchemaId,
    forceVisibleSelectedTagSchemaOrInterface: forceVisibleSelectedTagSchema,
    isTagSchemaOrInterfaceDisabledFn: transformTagSchemaFunctionToTagSchemaOrInterfaceFunction<boolean>(
      isTagSchemaDisabledFn,
      true
    ),
    tagSchemaFilterFn: transformTagSchemaFunctionToTagSchemaOrInterfaceFunction<boolean>(
      tagSchemaFilterFn,
      false
    ),
    allowInterfaces: false,
    allowNone,
  };
};

export function TagSchemaSelector({
  allowNone = false,
  isDisabled = false,
  isTagSchemaDisabledFn = () => false,
  disabledMessage = '',
  oligoAwareFolderItemTypes,
  registryId,
  requireTranslationLink,
  requiredPermissions,
  selectedTagSchemaId,
  forceVisibleSelectedTagSchema,
  onChange,
  tagSchemaFilterFn,
  starredSchemaIds,
  tagSchemas,
  getSchemaOwner,
  shouldDefaultIfSingleItem,
}: TagSchemaSelectorProps & ConnectedProps): JSX.Element {
  return (
    <TagSchemaOrInterfaceSelector
      {...transformTagSchemaSelectorPropsToTagSchemaOrInterfaceSelectorProps(
        onChange,
        selectedTagSchemaId,
        forceVisibleSelectedTagSchema,
        isTagSchemaDisabledFn,
        tagSchemaFilterFn,
        allowNone
      )}
      tagSchemasOrInterfaces={tagSchemas}
      starredSchemaIds={starredSchemaIds}
      getSchemaOwner={getSchemaOwner}
      isDisabled={isDisabled}
      disabledMessage={disabledMessage}
      oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
      registryId={registryId}
      requireTranslationLink={requireTranslationLink}
      requiredPermissions={requiredPermissions}
      shouldDefaultIfSingleItem={shouldDefaultIfSingleItem}
    />
  );
}

export default function QueryingTagSchemaSelector({
  allowNone = false,
  isDisabled = false,
  isTagSchemaDisabledFn = () => false,
  disabledMessage = '',
  oligoAwareFolderItemTypes,
  registryId,
  requireTranslationLink,
  requiredPermissions,
  selectedTagSchemaId,
  forceVisibleSelectedTagSchema,
  onChange,
  tagSchemaFilterFn,
  showLoadingIndicator,
  shouldDefaultIfSingleItem,
}: TagSchemaSelectorProps): JSX.Element {
  if (forceVisibleSelectedTagSchema && selectedTagSchemaId) {
    assertAndContinue(
      forceVisibleSelectedTagSchema.id === selectedTagSchemaId,
      `selectedTagSchemaId (${selectedTagSchemaId}) and the ID of forceVisibleSelectedTagSchema (${forceVisibleSelectedTagSchema.id}) must match`
    );
  }

  return (
    <QueryingTagSchemaOrInterfaceSelector
      {...transformTagSchemaSelectorPropsToTagSchemaOrInterfaceSelectorProps(
        onChange,
        selectedTagSchemaId,
        forceVisibleSelectedTagSchema,
        isTagSchemaDisabledFn,
        tagSchemaFilterFn,
        allowNone
      )}
      isDisabled={isDisabled}
      disabledMessage={disabledMessage}
      oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
      registryId={registryId}
      requireTranslationLink={requireTranslationLink}
      requiredPermissions={requiredPermissions}
      showLoadingIndicator={showLoadingIndicator}
      shouldDefaultIfSingleItem={shouldDefaultIfSingleItem}
    />
  );
}
